import React, { useMemo } from "react";

import DataTable from "components/basic/data-table/DataTable";
import {
  MaintenanceDto,
  useGetObjectsByProjectQuery
} from "gql/generated/types";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { formatDateDuration, parseToLocalZoneDateTime } from "utils/time-utils";
import { Accordion, AccordionTab } from "primereact/accordion";
import useProjectContext from "../../hooks/useProjectContext";
import { maintenanceStatuses } from "../../utils/const";
import { SummaryMaintenance, TableColumn } from "../../utils/types";
import { apolloErrorToaster } from "../../utils/utils";

type MaintenanceSummaryProps = {
  data: MaintenanceDto[];
};

const statusForSummary = maintenanceStatuses
  .filter(
    s =>
      s.value === "DELAYED" ||
      s.value === "COORDINATED" ||
      s.value === "IN_PROGRESS" ||
      s.value === "PLANNED"
  )
  .toReversed();

const MaintenanceSummary: React.FC<MaintenanceSummaryProps> = ({ data }) => {
  const { projectId } = useProjectContext();
  const { data: objects, loading } = useGetObjectsByProjectQuery({
    variables: {
      id: projectId!,
    },
    skip: !projectId,
    onError: apolloErrorToaster,
  });

  const formatData = useMemo(
    () =>
      objects
        ? data.reduce((acc, m) => {
            if (m.objectIds && m.objectIds.length > 0) {
              m.objectIds.forEach(objectId => {
                if (!objectId) return;
                const object = objects?.mdmObjectsByProjectId.find(o => o.id === objectId);
                const transformedObject: SummaryMaintenance = {
                  id: objectId,
                  identifier: object?.identifier!,
                  purpose: object?.customFields?.find(f => f?.key === "object_purpose")?.value?.value ?? "",
                  address: object?.address?.fullAddress!,
                  maintenance: m,
                };
                acc.push(transformedObject);
              });
            }
            return acc;
          }, [] as SummaryMaintenance[])
        : [],
    [data, objects]
  );

  const objectInfoBtn = (rowData: SummaryMaintenance) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.id}`} title={T("mdm.object-info")}>
      {rowData.identifier}
    </Link>
  );

  const objectNameTemplate = (rowData: SummaryMaintenance) => (
    <div className="truncate-text text-right lg:text-left" title={rowData.purpose ?? ""}>
      {rowData.purpose}
    </div>
  );

  const renderLink = (rowData: SummaryMaintenance) => (
    <div className="flex flex-column align-items-center">
      <Link to={rowData.maintenance.id!}>{rowData.maintenance.title}</Link>
      <span>
        {T("word.disconnection-time")}{": "}
        {rowData.maintenance.disconnectionTime
          ? parseToLocalZoneDateTime(rowData.maintenance.disconnectionTime).format(
              DEFAULT_DATE_FORMATTER
            )
          : "-"}
      </span>
      <span>
        {T("word.reconnection-time")}{": "}
        {rowData.maintenance.reconnectionTime
          ? parseToLocalZoneDateTime(rowData.maintenance.reconnectionTime).format(
              DEFAULT_DATE_FORMATTER
            )
          : "-"}
      </span>
      <span>
        {T("word.duration")}{": "}
        {formatDateDuration(rowData.maintenance.disconnectionTime, rowData.maintenance.reconnectionTime)}
      </span>
    </div>
  );

  const columns: TableColumn<SummaryMaintenance>[] = [
    {
      field: "identifier",
      header: T("mdm.object-code"),
      body: objectInfoBtn,
      sortable: true,
      columnKey: "identifier",
    },
    {
      field: "purpose",
      header: T("word.object-purpose"),
      body: objectNameTemplate,
      sortable: true,
      columnKey: "objectPurpose",
    },
    {
      field: "address",
      header: T("word.address"),
      sortable: true,
      columnKey: "address",
    },
    {
      field: "maintenance.disconnectionTime",
      columnKey: "disconnectionTime",
      header: T("word.disconnection-time"),
      sortable: true,
      hidden: true
    },
    {
      field: "maintenance.id",
      header: T("mdm.maintenance"),
      body: renderLink,
      columnKey: "maintenanceId",
      sortable: true,
      sortField: "maintenance.disconnectionTime"
    },
  ];

  return (
    <div className="flex flex-column gap-3">
      <Accordion activeIndex={0}>
      {statusForSummary.map(s => {
        const filteredData = formatData.filter(m => m.maintenance.status === s.value);
        return (
          <AccordionTab
            key={s.value}
            header={
              <span>
                <span className="text-white px-2 py-1 border-round" style={{ backgroundColor: s.bgColor}}>{T(s.translationKey)}</span>
                {" - "}
                {loading ? <i className="pi pi-spin pi-spinner" /> : filteredData.length}
              </span>
            }
          >
            <DataTable
              key={s.value}
              columns={columns}
              data={filteredData}
              tableConfig={{
                rowGroupMode: "rowspan",
                loading,
                groupRowsBy: "maintenance.id",
                sortField: "maintenance.disconnectionTime",
                sortOrder: 1,
              }}
            />
          </AccordionTab>
        );
      })}
      </Accordion>
    </div>
  );
};

export default MaintenanceSummary;

